import { tierById, tierLineItems, unitVariantIds, withTierLines } from './offers.logic.js';
/**
 * 🚨 THE one definition of "what this order contains". Pure, and shared by everything that needs it.
 *
 * There were two before — one in `blocks.ts` for the summary, one in `FormSession` for the payload
 * — and they disagreed three separate times, each time silently and each time in the buyer's
 * disfavour:
 *
 *  | Symptom                                              | Which copy was wrong |
 *  | ---------------------------------------------------- | -------------------- |
 *  | cart order created for one product, the rest dropped | payload              |
 *  | tier picked, order made for 1 unit of the default    | payload              |
 *  | quantity stepped up, order still made for 1 unit     | payload              |
 *
 * The display copy was right every time, which is exactly why it kept shipping: the merchant sees
 * the summary, not the payload. So the rule now lives here and both callers delegate — a divergence
 * has to be a deliberate edit to one function rather than a forgotten one.
 *
 * Precedence, in order:
 *  1. lines the buyer dropped with the per-item ✕ are gone
 *  2. an active tier REPLACES the page product's lines (`withTierLines`)
 *  3. a ONE-line cart takes its quantity from the stepper
 *  4. no cart at all → the tier's lines, or the single page product
 */
export function orderLineItems(config, ctx, state, offer) {
    const all = ctx.lineItems ?? [];
    const removed = state.removedVariantIds ?? [];
    const kept = removed.length
        ? all.filter((li) => li.variantId == null || !removed.includes(li.variantId))
        : all;
    const tier = offer ? tierById(offer, state.selectedTierId) : undefined;
    const ids = tier ? unitVariantIds(tier, ctx, state.tierVariantIds) : [];
    if (all.length) {
        if (tier)
            return withTierLines(kept, ctx, tier, ids);
        if (singleLineStepper(config, ctx, state, offer) && kept.length === 1) {
            return [{ ...kept[0], quantity: Math.max(1, state.quantity) }];
        }
        return kept;
    }
    if (tier)
        return tierLineItems(tier, ctx, ids);
    return [
        {
            id: ctx.product.id,
            variantId: ctx.variant.id,
            name: ctx.product.name,
            quantity: Math.max(1, state.quantity),
            price: ctx.variant.price,
            ...(ctx.variant.name ? { variant: ctx.variant.name } : {}),
            ...(ctx.product.image ? { image: ctx.product.image } : {}),
        },
    ];
}
/**
 * Whether a one-line cart form shows the quantity stepper.
 *
 * Only for a SINGLE line, and only when the merchant has not hidden the controls. A tier owns the
 * quantity while it is selected, so the stepper stands down for it.
 *
 * 🚨 Keyed on the ORIGINAL line count, never on what survives the removals. `state.quantity` is
 * seeded from the single line when the form opens, so it only means that line's quantity if there
 * was one line to begin with. Keying on the filtered list let a two-line cart become one-line via
 * the ✕ and then clobber the survivor's quantity with an unseeded `1` — a test caught it turning a
 * "× 3" line into "× 1", with the total following it down.
 */
export function singleLineStepper(config, ctx, state, offer) {
    if (config.product_card.hide_quantity_controls)
        return false;
    if (offer && tierById(offer, state.selectedTierId))
        return false;
    return (ctx.lineItems ?? []).length === 1;
}
//# sourceMappingURL=lines.js.map